import React, { useCallback } from 'react'
import classNames from 'classnames'
import { Button } from '../../../../components/Button/Button'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useLazyGetAllCrossClientQueryExecutionResultsQuery } from '../../../../store/crossClient/crossClient'
import { CcqCards, cardForResult } from './ccqResultView'
import { CcqActionButtons } from './CcqActionButtons'
import { CCQ_CLEAR_SELECTION, CCQ_FOOTER_ALL_ACTIONED, CCQ_FOOTER_RUNNING, ccqFooterDone, ccqFooterSelected } from './ccqRunCopy'
import { collectResultsByIds, useCcqCollectCardResults, useCcqWithActionsCount } from './useCcqCardResults'
import { useCcqRun } from './useCcqRun'
import { useCcqCursor } from './useCcqListCursor'
import { useCcqWorkspace } from './useCcqWorkspace'
import styles from './CcqGroupCard.module.scss'

const EMPTY_SELECTION: string[] = []

interface CcqMatchedFooterProps {
  isSticky: boolean
}

export const CcqMatchedFooter: React.FC<CcqMatchedFooterProps> = ({ isSticky }) => {
  const accountId = useAppSelector(selectAccountId)
  const { isDone, counts, execution } = useCcqRun()
  const { selection: workspaceSelection, clearSelection } = useCcqWorkspace()
  const fullListCard = useCcqCursor().list.card
  const selection = fullListCard === CcqCards.MATCHED ? workspaceSelection : EMPTY_SELECTION
  const collectCardResults = useCcqCollectCardResults()
  const [triggerGetAll] = useLazyGetAllCrossClientQueryExecutionResultsQuery()
  const withActions = useCcqWithActionsCount(CcqCards.MATCHED)
  const total = counts.matched
  const hasSelection = selection.length > 0
  // Live counts, not execution.matches: that snapshot isn't refreshed once the check is done.
  const everyMatchHasAction = isDone && total > 0 && withActions >= total

  // A selected client who has since left Matched (moved by hand or by AI) is left out.
  const resolveTargets = useCallback(async () => {
    if (selection.length > 0) {
      const results = await collectResultsByIds(triggerGetAll, { accountId, executionId: execution.id }, selection)
      return results.filter((result) => cardForResult(result) === CcqCards.MATCHED)
    }
    return collectCardResults(CcqCards.MATCHED, '', undefined)
  }, [selection, triggerGetAll, accountId, execution.id, collectCardResults])

  const message = (() => {
    if (!isDone) {
      return <span className='paragraph-small bold zocks-text-gray-600'>{CCQ_FOOTER_RUNNING}</span>
    }
    if (hasSelection) {
      return (
        <span className='d-flex align-items-center gap-2 paragraph-small bold zocks-text-gray-900'>
          {ccqFooterSelected(selection.length, total)}
          <Button variant='primaryLinkButton' className='button-small p-0 border-0' onClick={clearSelection}>
            {CCQ_CLEAR_SELECTION}
          </Button>
        </span>
      )
    }
    return <span className='paragraph-small bold zocks-text-gray-600'>{everyMatchHasAction ? CCQ_FOOTER_ALL_ACTIONED : ccqFooterDone(total)}</span>
  })()

  return (
    <div
      className={classNames(
        styles.footer,
        'd-flex align-items-center gap-3 zocks-py-0.75 zocks-pl-2.75 pe-3 border-top zocks-border-color-gray-200 zocks-bg-color-gray-100',
        { [styles.footerSticky]: isSticky },
      )}
    >
      <div className='flex-grow-1 mw-0'>{message}</div>
      <CcqActionButtons
        isPrimary={isDone && !everyMatchHasAction}
        isDisabled={!isDone}
        resolveTargets={resolveTargets}
        count={hasSelection ? selection.length : total}
        scope={hasSelection ? 'manual bulk' : 'recommended'}
        sendBookingInvitationsTarget={hasSelection ? { kind: 'results', resultIds: selection } : { kind: 'matched' }}
      />
    </div>
  )
}
